import type { Chapter } from '../model/playback.ts';
import { MAX_END_OVERRUN_SEC, parseTimecodeSeconds, TIMECODE_PATTERN } from '../text/timecode.ts';

/**
 * §7 — YouTube's chapter rules, applied to the description, **all-or-nothing**.
 *
 * The negatives are the whole feature. A description is uncontrolled text and timecodes appear in
 * it for every reason except chapters: a live channel lists `7:30 AM - 100 Days to Heaven`
 * programme slots (fixture `live-W3AoFKYu7wo`, 23 such lines), a tutorial writes ranges as
 * `Retrac Download - 00:13 - 00:59`, a music video quotes a running time. Rendering *some* of a
 * chapter bar is worse than rendering none: the segments are wrong widths, the titles sit under
 * the wrong footage, and nothing on screen says so. So any failed check discards the whole set.
 *
 * Both passes read the **plain** `description`. `descriptionHtml`'s own timestamp anchors are
 * shifted (see `parse/richText.ts`) — `00<a …>:00:00 C</a>ourse Intro` — so a parser reading them
 * would take `:00:00 C` as the timecode text and `ourse Intro` as the title.
 */

/**
 * **A chapter has no minimum length here.** YouTube publishes one — ten seconds — and then renders
 * lists that break it, and its watch page is the only authority this parser can measure against.
 * TechLinked's `/watch?v=raO17F9uINs` (497 s) writes `4:50 QUICK BITS INTRO` and cuts to the next
 * story eight seconds later; YouTube draws all ten chapters. `/watch?v=l-3ugGYIYMo` (444 s) has two
 * segments under the floor and YouTube draws all ten of those as well, and `/watch?v=mikDGdoFGmw`
 * (605 s) closes on `10:03 Credits` two seconds from the end and gets its cell too. A stinger
 * between segments and a credits card at the end are what a daily bulletin is made of, so the floor
 * was taking ten of that channel's last fourteen uploads from a complete bar to none.
 *
 * Removing it gives up less than it looks like, because the floor was never what refuses a list of
 * timecodes that is not a chapter list. A programme schedule or a tracklist is refused by
 * {@link MAX_FIRST_CHAPTER_SEC}, prose order by the strictly-ascending rule in {@link collect}, and a
 * run of range ends by {@link toChapters}' last-start-inside-the-video rule — every real negative in
 * the corpus fails on one of those and none of them on the floor. What it added on top was the power
 * to throw away a list that was right about every other line.
 */
const MIN_CHAPTERS = 3;

/**
 * How late the first timecode may be and still open a list. YouTube demands exactly `0:00`; this
 * allows the opening half-minute, and the chapter it makes still starts at 0 (see {@link toChapters}).
 *
 * An uploader who never labels the greeting is the shape that asked for it: GameStar's daily bulletin
 * (`/watch?v=yie9pGpPjDw` — 523 s) writes six clean lines from `00:23`, and the whole list was being
 * discarded over the 23 seconds of intro nobody named. A leading gap is an unlabelled opening, not
 * evidence that the list is a schedule.
 *
 * **30 s is the bound, and it is what keeps the rule from reopening the door the `0:00` check exists
 * to hold.** A tracklist or a programme schedule does not start in the first half-minute — the live
 * fixture's first slot is `7:30`, and a music album's is past a minute — so they are refused exactly
 * as before, by this check rather than by chance.
 */
const MAX_FIRST_CHAPTER_SEC = 30;
/**
 * A chapter line is a timecode and a short title. A 400-character paragraph that happens to end
 * in `12:34` is prose, and letting it in is how a credits roll becomes a chapter bar.
 */
const MAX_LINE_LENGTH = 200;

/**
 * How many stamps may go **backwards** and lose only their own line. One, and the shape that asked
 * for it is a swapped pair: GameStar's bulletin `/watch?v=eJp1PWWYip4` (560 s) writes seven clean
 * lines and then closes `08:19 - Gaming-Deals` before `07:59 - Outro`. YouTube renders that video
 * with seven chapters and no outro, so the line the uploader mis-stamped is the line that goes, and
 * the list it sits in is right about everything else.
 *
 * **One is the bound, and it is what keeps this from reopening the door the ascending rule exists to
 * hold.** A list that is not a chapter list has its timecodes in whatever order the prose put them,
 * so it inverts again within a line or two and poisons the pass exactly as before; a mis-stamped
 * line inverts once. The order the uploader wrote is still the evidence, and the list is still never
 * sorted — a second stamp going backwards is a list going backwards.
 */
const MAX_BACKWARDS_LINES = 1;

/**
 * Bullets and separators an uploader puts *around* the timecode. Never digits or `:`.
 *
 * The blocks at the end are the pictograph half, and a real description is what asked for them:
 * MeinMMO opens every chapter line with `➡️` (`/watch?v=AIjMc3x0PVk`), and one arrow this parser did
 * not know discarded a list that was right about all seven of its lines. Whole blocks rather than the
 * characters seen so far — arrows, technical symbols, geometric shapes, dingbats and the emoji
 * planes, plus the variation selectors and the joiner that dress them — because the next uploader
 * picks a different pictograph, and a class extended one character at a time is a parser that breaks
 * again next month. None of the blocks contains a digit or a colon, so the rules below still decide
 * what is a chapter list.
 *
 * `▶ ► →` need no entry of their own any more; the ranges hold them. The surrogate halves are spelled
 * out because these patterns carry no `u` flag, so a pictograph outside the BMP counts as **two**
 * characters — which is why the bound is eight rather than the four an ASCII bullet needs.
 */
const DECORATION =
  '[\\s\\-–—‑•·*»|>()[\\]#\\u2190-\\u21ff\\u2300-\\u23ff\\u25a0-\\u27bf\\u2b00-\\u2bff' +
  '\\u200d\\ufe0e\\ufe0f\\ud83c-\\ud83e\\udc00-\\udfff]';

const LEADING_RE = new RegExp(`^${DECORATION}{0,8}(${TIMECODE_PATTERN})(?![\\d:])([\\s\\S]*)$`);
const TRAILING_RE = new RegExp(`^(.*[^\\d:])(${TIMECODE_PATTERN})[\\s)\\]]*$`);
const HAS_URL = /https?:\/\/|www\./i;

/**
 * The **to** half of a from-to line — `0:00-1:05 Intro`. `/watch?v=SCI8g6EinAo` writes ten of its
 * eleven chapters that way and the last one plainly, so the strip is per line rather than per
 * description. Without it the title reads `1:05 Intro`, because `cleanTitle` takes the dash off and
 * leaves the number behind it standing.
 *
 * A **dash** is required between the two, not merely a space: `0:00 1:05 Intro` is ambiguous, and a
 * rule that ate any second timecode would eat the opening word of a title that happens to start with
 * one. The declared end is then discarded rather than used — a chapter still ends where the next one
 * starts (`durationSec` for the last), so there is one source for a boundary instead of two that can
 * disagree and leave a gap the bar has no way to draw.
 *
 * **The leading pass only.** In the trailing form the two timecodes are the same pair the other way
 * round (`Retrac Download - 00:13 - 00:59`), and there the *last* one is what the pattern reads, so
 * the list would start at the first chapter's end. §7 refuses that rather than guessing which of the
 * pair was meant, and this does not reopen the question.
 */
const RANGE_END_RE = new RegExp(`^\\s*[-–—‑]\\s*(?:${TIMECODE_PATTERN})(?![\\d:])`);

interface Candidate {
  readonly seconds: number;
  readonly title: string;
}

function cleanTitle(raw: string): string {
  let title = raw.trim();
  title = title.replace(/^[\s\-–—‑:•·*▶►»→|>)\]]+/, '');
  title = title.replace(/[\s\-–—‑:•·*|({[]+$/, '');
  const wrapped = /^[([{"'](.*)[)\]}"']$/.exec(title);
  if (wrapped) title = (wrapped[1] ?? '').trim();
  return title.trim();
}

/**
 * `null` means "this pass is dead", which is different from "this line is not a chapter". A line
 * that looks like a chapter but breaks a rule — carries a URL, runs to 400 characters — poisons
 * the pass rather than being skipped, because skipping it would silently drop one segment out of
 * a bar that then renders as if it were complete.
 *
 * A stamp that does not **advance** is the exception, and it comes in two sizes.
 *
 * A **repeat** — a timecode equal to the one before it — is skipped freely, the earlier title
 * standing. `/watch?v=-EMKMPxJrWY` writes `09:37` twice among its sixteen lines, one label the
 * uploader wrote and one from whatever produced the rest. Two labels for the same instant is a
 * duplicate to drop, not evidence that the list is a schedule, and dropping one costs nothing a bar
 * can draw: a chapter of zero length has no width.
 *
 * A stamp that goes **backwards** loses its line too, but only {@link MAX_BACKWARDS_LINES} of them
 * may — one mis-stamped line is a typo, and a second is a list whose timecodes are in prose order.
 * Everything else still poisons the pass.
 *
 * This is where the strictly-ascending rule is enforced, so what {@link toChapters} receives is
 * already ascending in description order. **Never sorted**: sorting would turn a schedule into a
 * plausible bar, and it would file a mis-stamped line under a heading it does not label.
 */
function collect(lines: readonly string[], trailing: boolean): readonly Candidate[] | null {
  const found: Candidate[] = [];
  let backwards = 0;

  for (const line of lines) {
    const match = (trailing ? TRAILING_RE : LEADING_RE).exec(line);
    if (!match) continue;

    if (line.length > MAX_LINE_LENGTH) return null;
    if (HAS_URL.test(line)) return null;

    const seconds = parseTimecodeSeconds((trailing ? match[2] : match[1]) ?? '');
    if (seconds === null) return null;

    const previous = found[found.length - 1]?.seconds;
    if (previous !== undefined && seconds === previous) continue;
    if (previous !== undefined && seconds < previous) {
      backwards += 1;
      if (backwards > MAX_BACKWARDS_LINES) return null;
      continue;
    }

    const rest = (trailing ? match[1] : match[2]) ?? '';
    found.push({ seconds, title: cleanTitle(trailing ? rest : rest.replace(RANGE_END_RE, '')) });
  }

  return found;
}

/**
 * A **single final** candidate standing at or past the end of the video, by no more than
 * {@link MAX_END_OVERRUN_SEC}, is dropped before the rules run — the last narrow exception to
 * "a line that breaks a rule poisons the pass", beside the two in {@link collect}.
 *
 * The shape that asked for it: GameStar's bulletin `/watch?v=MLvo2uylstg` (491.5 s of footage,
 * `lengthSeconds` 492) closes its six clean lines with `08:17 - Outro` — an outro the final cut
 * stops five seconds short of. The whole list was refused over the one line that labels no
 * footage, and dropping that line costs nothing a bar can draw. Only the final candidate is ever
 * dropped: with two out of range, the one left standing still fails {@link toChapters}'
 * last-start-inside-the-video rule, and a stamp past the slack is left standing too, so a
 * tracklist that merely *ends* near the video's length is refused exactly as before.
 *
 * **The leading pass only**, for {@link RANGE_END_RE}'s reason: in the trailing form a from-to
 * line's *last* timecode is what the pattern reads, and the final range of a list ends exactly at
 * the video's end — so dropping it would hand the rules a list of range *ends* to read as starts,
 * which is the partial bar §7 exists to refuse (`Retrac Download - 00:13 - 00:59`, its last line
 * ending at 03:58 in a 238 s video).
 */
function withoutEndOverrun(candidates: readonly Candidate[], durationSec: number): readonly Candidate[] {
  const last = candidates[candidates.length - 1];
  if (last === undefined || last.seconds < durationSec) return candidates;
  if (last.seconds > durationSec + MAX_END_OVERRUN_SEC) return candidates;
  return candidates.slice(0, -1);
}

/**
 * The rules that read the list as a whole. `listed` is strictly ascending in description order
 * already — {@link collect} is what enforces that, and {@link withoutEndOverrun} only ever drops
 * from the end — so what is left here is the shape of the list against the video.
 */
function toChapters(listed: readonly Candidate[], durationSec: number): readonly Chapter[] | null {
  // The count is taken *after* any overrun drop, so a dropped stamp cannot stand in for a third
  // chapter.
  if (listed.length < MIN_CHAPTERS) return null;

  const first = listed[0];
  // A list starting at 1:30 is a tracklist or a schedule. One starting inside the opening half-minute
  // is a list whose greeting went unlabelled — see {@link MAX_FIRST_CHAPTER_SEC}.
  if (first === undefined || first.seconds > MAX_FIRST_CHAPTER_SEC) return null;

  const last = listed[listed.length - 1];
  if (last === undefined || last.seconds >= durationSec) return null;

  const chapters: Chapter[] = [];
  for (let i = 0; i < listed.length; i += 1) {
    const candidate = listed[i];
    if (candidate === undefined) return null;
    if (candidate.title === '') return null;

    const endSec = listed[i + 1]?.seconds ?? durationSec;

    // **The opening chapter starts at 0, whatever the uploader wrote.** A late first timecode is
    // what {@link MAX_FIRST_CHAPTER_SEC} accepts, and it is evidence the list is real; it is not a
    // hole to carry downstream. `timelineSegments` tiles the bar from the chapters it is given and
    // maps a scrub back through the full duration, so cells that began at 0:23 would sit 23 s left
    // of the footage they label, and `chapterIndexAt` would answer `null` for the opening seconds —
    // no active cell, no title under the bar. Both invariants are cheaper to keep here than to
    // re-derive at every reader.
    chapters.push({ title: candidate.title, startSec: i === 0 ? 0 : candidate.seconds, endSec });
  }

  return chapters;
}

/**
 * Chapters, or an empty list. Two passes, never mixed: leading (`0:00 Intro`, and `0:00-1:05 Intro`
 * with it — see {@link RANGE_END_RE}) first, then trailing (`Intro — 0:00`).
 *
 * `durationSec` is required and load-bearing — it is what rejects a live stream's programme
 * schedule, since a live video reports `lengthSeconds: 0` and no chapter can then be inside it.
 */
export function parseChapters(description: string, durationSec: number): readonly Chapter[] {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return [];

  // §7: non-breaking spaces. Invisible, and they defeat every `\s`-shaped rule below.
  const lines = description.replace(/\u00a0/g, ' ').split(/\r\n|\r|\n/);

  for (const trailing of [false, true]) {
    const candidates = collect(lines, trailing);
    if (candidates === null) continue;
    const listed = trailing ? candidates : withoutEndOverrun(candidates, durationSec);
    const chapters = toChapters(listed, durationSec);
    if (chapters !== null) return chapters;
  }

  return [];
}
